import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { formatCurrency } from '../../../lib/formatters.js';

interface DecisionActionBarProps {
  claimId: string;
  claimedAmount: number;
  isoCurrencyCode: string;
  maxApprovalAmount?: number | null;
  currentStatus: string;
  onDecisionMade: () => void;
}

type ActivePanel =
  | 'APPROVE'
  | 'PARTIAL_APPROVE'
  | 'REJECT'
  | 'REQUEST_MORE_EVIDENCE'
  | 'ESCALATE'
  | null;

const REJECT_REASONS = [
  'INSUFFICIENT_EVIDENCE',
  'PERIOD_MISMATCH',
  'SCHEME_INELIGIBLE',
  'AMOUNT_OVERSTATED',
  'DUPLICATE',
  'OTHER',
] as const;

type RejectReason = (typeof REJECT_REASONS)[number];

const FINAL_STATUSES = new Set([
  'APPROVED',
  'PARTIALLY_APPROVED',
  'REJECTED',
  'PAID',
  'PAYMENT_QUEUED',
  'CANCELLED',
]);

export function DecisionActionBar({
  claimId,
  claimedAmount,
  isoCurrencyCode,
  maxApprovalAmount,
  currentStatus,
  onDecisionMade,
}: DecisionActionBarProps) {
  const { t } = useTranslation();
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Partial approve state
  const [partialAmount, setPartialAmount] = useState<string>(String(claimedAmount));
  const [partialReason, setPartialReason] = useState('');

  // Reject state
  const [rejectReason, setRejectReason] = useState<RejectReason>('INSUFFICIENT_EVIDENCE');
  const [rejectComment, setRejectComment] = useState('');

  // Request more evidence state
  const [evidenceMessage, setEvidenceMessage] = useState('');

  const isFinal = FINAL_STATUSES.has(currentStatus);
  const showEscalate =
    maxApprovalAmount != null && claimedAmount > maxApprovalAmount;

  function togglePanel(panel: ActivePanel) {
    setActivePanel((prev) => (prev === panel ? null : panel));
    setError(null);
  }

  async function postDecision(body: Record<string, unknown>) {
    setSubmitting(true);
    setError(null);
    try {
      await api.post<unknown>(`/approver/claims/${claimId}/decisions`, body);
      setActivePanel(null);
      onDecisionMade();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.internalServerError'));
    } finally {
      setSubmitting(false);
    }
  }

  if (isFinal) {
    return (
      <div className="px-4 py-3 border border-hairline rounded bg-bone text-center">
        <p className="text-sm text-slate-500 italic">
          {t('approver.actions.alreadyDecided', {
            defaultValue: 'This claim has already been decided.',
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Button row */}
      <div className="flex flex-wrap gap-2">
        {/* Approve */}
        <button
          type="button"
          onClick={() => togglePanel('APPROVE')}
          className={[
            'px-4 py-2 rounded text-sm font-semibold transition-colors',
            activePanel === 'APPROVE'
              ? 'bg-success text-white ring-2 ring-success/30'
              : 'bg-success text-white hover:opacity-90',
          ].join(' ')}
        >
          {t('approver.actions.APPROVE', { defaultValue: 'Approve' })}
        </button>

        {/* Partial Approve */}
        <button
          type="button"
          onClick={() => togglePanel('PARTIAL_APPROVE')}
          className={[
            'px-4 py-2 rounded text-sm font-semibold transition-colors border',
            activePanel === 'PARTIAL_APPROVE'
              ? 'bg-copper-soft border-copper text-copper-deep ring-2 ring-copper/20'
              : 'bg-copper-soft border-copper text-copper-deep hover:bg-copper/10',
          ].join(' ')}
        >
          {t('approver.actions.PARTIAL_APPROVE', { defaultValue: 'Partial Approve' })}
        </button>

        {/* Reject */}
        <button
          type="button"
          onClick={() => togglePanel('REJECT')}
          className={[
            'px-4 py-2 rounded text-sm font-semibold transition-colors border',
            activePanel === 'REJECT'
              ? 'border-danger text-danger bg-danger/5 ring-2 ring-danger/20'
              : 'border-danger text-danger hover:bg-danger/5',
          ].join(' ')}
        >
          {t('approver.actions.REJECT', { defaultValue: 'Reject' })}
        </button>

        {/* Request More Evidence */}
        <button
          type="button"
          onClick={() => togglePanel('REQUEST_MORE_EVIDENCE')}
          className={[
            'px-4 py-2 rounded text-sm font-semibold transition-colors border border-hairline',
            activePanel === 'REQUEST_MORE_EVIDENCE'
              ? 'text-slate-700 bg-slate-100 ring-2 ring-slate-200'
              : 'text-slate-600 hover:bg-slate-50',
          ].join(' ')}
        >
          {t('approver.actions.REQUEST_MORE_EVIDENCE', { defaultValue: 'Request More Evidence' })}
        </button>

        {/* Escalate (conditional) */}
        {showEscalate && (
          <button
            type="button"
            onClick={() => togglePanel('ESCALATE')}
            className={[
              'px-4 py-2 rounded text-sm font-semibold transition-colors border border-hairline',
              activePanel === 'ESCALATE'
                ? 'text-slate-700 bg-slate-100 ring-2 ring-slate-200'
                : 'text-slate-600 hover:bg-slate-50',
            ].join(' ')}
          >
            {t('approver.actions.ESCALATE', { defaultValue: 'Escalate' })}
          </button>
        )}
      </div>

      {/* Inline confirmation panels */}
      {activePanel === 'APPROVE' && (
        <div className="border border-success/30 rounded bg-success/5 p-4 space-y-3">
          <p className="text-sm font-medium text-ink">
            {t('approver.actions.confirmApprove', {
              defaultValue: 'Approve full claimed amount?',
            })}
          </p>
          <p className="font-mono text-sm font-semibold text-ink">
            {formatCurrency(claimedAmount, isoCurrencyCode)}
          </p>
          {error && <p className="text-xs text-danger">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={submitting}
              onClick={() => {
                void postDecision({ decision: 'APPROVE' });
              }}
              className="px-4 py-2 bg-success text-white rounded text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {submitting
                ? t('approver.actions.submitting', { defaultValue: 'Submitting...' })
                : t('approver.actions.confirmBtn', { defaultValue: 'Confirm Approve' })}
            </button>
            <button
              type="button"
              onClick={() => setActivePanel(null)}
              className="px-4 py-2 border border-hairline rounded text-sm text-slate-500 hover:bg-slate-50 transition-colors"
            >
              {t('approver.actions.cancel', { defaultValue: 'Cancel' })}
            </button>
          </div>
        </div>
      )}

      {activePanel === 'PARTIAL_APPROVE' && (
        <div className="border border-copper/30 rounded bg-copper-soft p-4 space-y-3">
          <p className="text-sm font-medium text-ink">
            {t('approver.actions.partialApproveTitle', { defaultValue: 'Enter approved amount' })}
          </p>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide block mb-1">
                {t('approver.actions.approvedAmount', { defaultValue: 'Approved Amount (IDR)' })}
              </label>
              <input
                type="number"
                min={0}
                max={claimedAmount}
                value={partialAmount}
                onChange={(e) => setPartialAmount(e.target.value)}
                className="w-full px-3 py-2 border border-hairline rounded text-sm font-mono bg-bone-soft focus:outline-none focus:border-copper"
                placeholder={String(claimedAmount)}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide block mb-1">
                {t('approver.actions.reason', { defaultValue: 'Reason (required)' })}
              </label>
              <input
                type="text"
                value={partialReason}
                onChange={(e) => setPartialReason(e.target.value)}
                className="w-full px-3 py-2 border border-hairline rounded text-sm bg-bone-soft focus:outline-none focus:border-copper"
                placeholder={t('approver.actions.reasonPlaceholder', {
                  defaultValue: 'Explain the partial amount...',
                })}
              />
            </div>
          </div>
          {error && <p className="text-xs text-danger">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={submitting || !partialReason.trim() || !partialAmount}
              onClick={() => {
                void postDecision({
                  decision: 'PARTIAL_APPROVE',
                  approvedAmount: Number(partialAmount),
                  reason: partialReason.trim(),
                });
              }}
              className="px-4 py-2 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm transition-colors disabled:opacity-50"
            >
              {submitting
                ? t('approver.actions.submitting', { defaultValue: 'Submitting...' })
                : t('approver.actions.confirmPartial', { defaultValue: 'Confirm Partial Approve' })}
            </button>
            <button
              type="button"
              onClick={() => setActivePanel(null)}
              className="px-4 py-2 border border-hairline rounded text-sm text-slate-500 hover:bg-slate-50 transition-colors"
            >
              {t('approver.actions.cancel', { defaultValue: 'Cancel' })}
            </button>
          </div>
        </div>
      )}

      {activePanel === 'REJECT' && (
        <div className="border border-danger/30 rounded bg-danger/5 p-4 space-y-3">
          <p className="text-sm font-medium text-ink">
            {t('approver.actions.rejectTitle', { defaultValue: 'Select rejection reason' })}
          </p>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide block mb-1">
                {t('approver.actions.rejectReason', { defaultValue: 'Reason' })}
              </label>
              <select
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value as RejectReason)}
                className="w-full px-3 py-2 border border-hairline rounded text-sm bg-bone-soft focus:outline-none focus:border-danger"
              >
                {REJECT_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {t(`approver.rejectReasons.${r}`, { defaultValue: r })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide block mb-1">
                {t('approver.actions.rejectComment', { defaultValue: 'Additional notes (optional)' })}
              </label>
              <textarea
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-hairline rounded text-sm bg-bone-soft focus:outline-none focus:border-danger resize-none"
                placeholder={t('approver.actions.rejectCommentPlaceholder', {
                  defaultValue: 'Optional explanation...',
                })}
              />
            </div>
          </div>
          {error && <p className="text-xs text-danger">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={submitting}
              onClick={() => {
                void postDecision({
                  decision: 'REJECT',
                  reason: rejectReason,
                  ...(rejectComment.trim() ? { comment: rejectComment.trim() } : {}),
                });
              }}
              className="px-4 py-2 bg-danger text-white rounded text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {submitting
                ? t('approver.actions.submitting', { defaultValue: 'Submitting...' })
                : t('approver.actions.confirmReject', { defaultValue: 'Confirm Reject' })}
            </button>
            <button
              type="button"
              onClick={() => setActivePanel(null)}
              className="px-4 py-2 border border-hairline rounded text-sm text-slate-500 hover:bg-slate-50 transition-colors"
            >
              {t('approver.actions.cancel', { defaultValue: 'Cancel' })}
            </button>
          </div>
        </div>
      )}

      {activePanel === 'REQUEST_MORE_EVIDENCE' && (
        <div className="border border-hairline rounded bg-bone p-4 space-y-3">
          <p className="text-sm font-medium text-ink">
            {t('approver.actions.requestEvidenceTitle', {
              defaultValue: 'Request additional evidence',
            })}
          </p>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wide block mb-1">
              {t('approver.actions.evidenceMessage', { defaultValue: 'Message to submitter (required)' })}
            </label>
            <textarea
              value={evidenceMessage}
              onChange={(e) => setEvidenceMessage(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-hairline rounded text-sm bg-bone-soft focus:outline-none focus:border-copper resize-none"
              placeholder={t('approver.actions.evidenceMessagePlaceholder', {
                defaultValue: 'Describe what evidence is needed...',
              })}
            />
          </div>
          {error && <p className="text-xs text-danger">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={submitting || !evidenceMessage.trim()}
              onClick={() => {
                void postDecision({
                  decision: 'REQUEST_MORE_EVIDENCE',
                  comment: evidenceMessage.trim(),
                });
              }}
              className="px-4 py-2 border border-copper text-copper rounded text-sm font-semibold hover:bg-copper-soft transition-colors disabled:opacity-50"
            >
              {submitting
                ? t('approver.actions.submitting', { defaultValue: 'Submitting...' })
                : t('approver.actions.sendRequest', { defaultValue: 'Send Request' })}
            </button>
            <button
              type="button"
              onClick={() => setActivePanel(null)}
              className="px-4 py-2 border border-hairline rounded text-sm text-slate-500 hover:bg-slate-50 transition-colors"
            >
              {t('approver.actions.cancel', { defaultValue: 'Cancel' })}
            </button>
          </div>
        </div>
      )}

      {activePanel === 'ESCALATE' && (
        <div className="border border-hairline rounded bg-bone p-4 space-y-3">
          <p className="text-sm font-medium text-ink">
            {t('approver.actions.escalateTitle', { defaultValue: 'Escalate to senior approver?' })}
          </p>
          {maxApprovalAmount != null && (
            <p className="text-xs text-slate-500">
              {t('approver.actions.escalateReason', {
                defaultValue: 'The claimed amount exceeds your approval limit of',
              })}{' '}
              <span className="font-mono font-semibold">
                {formatCurrency(maxApprovalAmount, isoCurrencyCode)}
              </span>
              .
            </p>
          )}
          {error && <p className="text-xs text-danger">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={submitting}
              onClick={() => {
                void postDecision({ decision: 'ESCALATE' });
              }}
              className="px-4 py-2 border border-hairline text-slate-600 rounded text-sm font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              {submitting
                ? t('approver.actions.submitting', { defaultValue: 'Submitting...' })
                : t('approver.actions.confirmEscalate', { defaultValue: 'Confirm Escalate' })}
            </button>
            <button
              type="button"
              onClick={() => setActivePanel(null)}
              className="px-4 py-2 border border-hairline rounded text-sm text-slate-500 hover:bg-slate-50 transition-colors"
            >
              {t('approver.actions.cancel', { defaultValue: 'Cancel' })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
